// services/websocket.js
import io from 'socket.io-client';

class WebSocketService {
  constructor() {
    this.socket = null;
    this.listeners = new Map();
    this.isConnected = false;
  }

  // Initialize connection
  connect() {
    const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';
    
    this.socket = io(API_BASE_URL, {
      transports: ['websocket', 'polling'],
      cors: {
        origin: window.location.origin, // Dynamically use current origin
        credentials: true
      }
    });
    
    this.socket.on('connect', () => {
      console.log('🔌 WebSocket connected:', this.socket.id);
      this.isConnected = true;
      
      // Authenticate with token if available
      const token = localStorage.getItem('auth_token');
      if (token) {
        this.socket.emit('authenticate', token);
      }
    });

    this.socket.on('disconnect', () => {
      console.log('🔌 WebSocket disconnected');
      this.isConnected = false;
    });

    this.socket.on('connect_error', (error) => {
      console.error('❌ WebSocket connection error:', error);
      this.isConnected = false;
    });

    this.socket.on('auth_error', (error) => {
      console.error('❌ WebSocket auth error:', error);
    });

    // Set up complaint event listeners
    this.setupComplaintListeners();

    return this.socket;
  }

  // Set up listeners for complaint events
  setupComplaintListeners() {
    if (!this.socket) return;

    // Listen for new complaints
    this.socket.on('new_complaint', (data) => {
      console.log('📋 New complaint received:', data);
      this.notifyListeners('new_complaint', data);
    });

    // Listen for complaint updates
    this.socket.on('complaint_updated', (data) => {
      console.log('📋 Complaint updated:', data);
      this.notifyListeners('complaint_updated', data);
    });

    // Listen for import completion
    this.socket.on('import_complete', (data) => {
      console.log('📥 Import complete:', data);
      this.notifyListeners('import_complete', data);
    });

    // NEW: Listen for new messages in tickets
    this.socket.on('new_message', (data) => {
      console.log('📨 New message received:', data);
      this.notifyListeners('new_message', data);
    });
  }

  // Authenticate socket connection
  authenticate(token) {
    if (this.socket && this.isConnected) {
      this.socket.emit('authenticate', token);
    }
  }

  // NEW: Join a ticket room for real-time conversation updates
  joinTicket(ticketId) {
    if (this.socket && this.isConnected) {
      console.log('🎫 Joining ticket room:', ticketId);
      this.socket.emit('join_ticket', ticketId);
    } else {
      console.warn('⚠️ Cannot join ticket room - socket not connected');
    }
  }

  // NEW: Leave a ticket room
  leaveTicket(ticketId) {
    if (this.socket && this.isConnected) {
      console.log('🎫 Leaving ticket room:', ticketId);
      this.socket.emit('leave_ticket', ticketId);
    } else {
      console.warn('⚠️ Cannot leave ticket room - socket not connected');
    }
  }

  // NEW: Join a workspace room for workspace-specific updates
  joinWorkspace(workspaceId) {
    if (this.socket && this.isConnected) {
      console.log('🏢 Joining workspace room:', workspaceId);
      this.socket.emit('join_workspace', workspaceId);
    } else {
      console.warn('⚠️ Cannot join workspace room - socket not connected');
    }
  }

  // NEW: Leave a workspace room
  leaveWorkspace(workspaceId) {
    if (this.socket && this.isConnected) {
      console.log('🏢 Leaving workspace room:', workspaceId);
      this.socket.emit('leave_workspace', workspaceId);
    } else {
      console.warn('⚠️ Cannot leave workspace room - socket not connected');
    }
  }

  // Add event listener
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event).add(callback);
  }

  // Remove event listener
  off(event, callback) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).delete(callback);
    }
  }

  // Notify all listeners for an event
  notifyListeners(event, data) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`❌ Error in ${event} listener:`, error);
        }
      });
    }
  }

  // Disconnect
  disconnect() {
    if (this.socket) {
      console.log('🔌 Disconnecting WebSocket');
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
      this.listeners.clear();
    }
  }

  // Check if connected
  isSocketConnected() {
    return this.socket && this.isConnected;
  }

  // Get socket instance
  getSocket() {
    return this.socket;
  }
}

// Export singleton instance
const webSocketService = new WebSocketService();
export default webSocketService;